/*
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 *
 */

import ox from "$/ox";
import gt from "gettext";

import type { App } from "$/io.ox/core/desktop";
import FilesAPI from "$/io.ox/files/api";
import capabilities from "$/io.ox/core/capabilities";

import { is, to, str, set, fun, debug } from "@/io.ox/office/tk/algorithms";
import { type IconId, createElement, createAnchor, insertHiddenNodes } from "@/io.ox/office/tk/dom";
import { globalEvents } from "@/io.ox/office/tk/events";
import { LogLevel, Logger } from "@/io.ox/office/tk/utils/logger";
import { DEBUG } from "@/io.ox/office/tk/config";
import { AbortError } from "@/io.ox/office/tk/objects";

import type { BaseApplication } from "@/io.ox/office/baseframework/app/baseapplication";

// types ======================================================================

/**
 * Global configuration for the Documents portal and editor applications of a
 * specific type.
 */
export interface GlobalAppConfig {

    /**
     * The type identifier of the portal/editor application.
     */
    appType: AppType;

    /**
     * Relative index used to sort document actions in lists.
     */
    appIndex: number;

    /**
     * The identifier of an icon that will be shown in the "restore window" of
     * a minimized application.
     */
    appIcon: IconId;

    /**
     * The name of the CSS variable containing the color of the application
     * icon to be shown in the GUI.
     */
    appColor: string;

    /**
     * The translated title of the application to be shown in the GUI.
     */
    appTitle: string;

    /**
     * A regular expression matching the extensions of all file types supported
     * by the respective editor application.
     */
    appExtRE: RegExp;

    /**
     * All required capabilities for the Portal application.
     *
     * The portal applications are bound to the availability of the respective
     * editor application, and the Drive application (capability "infostore").
     * Furthermore, each portal application can be disabled with its own
     * capability "<apptype>portaldisabled", and needs a user account (not for
     * "guest" or "anonymous" sessions).
     */
    portalCapabilities: string;

    /**
     * Whether the Portal application is available according to the global
     * capabilities.
     */
    portalAvailable: boolean;

    /**
     * The module base path of the portal application.
     */
    portalModulePath: string;

    /**
     * The module base path of the editor application.
     */
    editorModulePath: string;

    /**
     * The UI name for the unencrypted document files.
     */
    docName: string;

    /**
     * The UI name for the encrypted document file.
     */
    docNameEncrypted: string;

    /**
     * The UI label for the action "Create new unencrypted document".
     */
    docNewLabel: string;

    /**
     * The UI label for the action "Create new encrypted document".
     */
    docNewEncryptedLabel: string;

    /**
     * The UI label for "Blank document" template.
     */
    docBlankLabel: string;
}

/**
 * The shape of the common debug main module with all registration entry points
 * for a Documents application.
 */
export type DebugCommonMainModule = typeof import("@/io.ox/office/debug/common/main");

// enums ----------------------------------------------------------------------

/**
 * Enumeration for all supported Documents applications. The string values of
 * the enumeration members are equal to the path names of the source modules,
 * and to the capability names of the respective apps.
 */
export enum AppType {

    /**
     * The text processing application.
     */
    TEXT = "text",

    /**
     * The spreadsheet editor application.
     */
    SPREADSHEET = "spreadsheet",

    /**
     * The presentation editor application.
     */
    PRESENTATION = "presentation",
}

/**
 * An enumeration for file formats supported by Documents editor applications.
 */
export enum FileFormatType {

    /**
     * Office Open XML file format (Microsoft Office).
     */
    OOX = "ooxml",

    /**
     * Open Document Format (OpenOffice, LibreOffice).
     */
    ODF = "odf"
}

/**
 * An enumeration for different origins causing to request a document flush
 * operation.
 */
export enum FlushReason {

    /**
     * The document needs to be downloaded ("Download", "Print" actions), or
     * copied ("Save As" actions).
     */
    DOWNLOAD = "download",

    /**
     * The document will be sent as an email attachment.
     */
    EMAIL = "email",

    /**
     * The document will be shared with other users.
     */
    SHARE = "share",

    /**
     * The application is shutting down.
     */
    QUIT = "quit",

    /**
     * The Presenter application will start a presentation for the document.
     */
    PRESENT = "present",

    /**
     * The document needs to be flushed due to some other user interaction,
     * e.g. activating the browser tab.
     */
    USER = "user"
}

/**
 * An enumeration for different reasons causing to shutdown the application.
 */
export enum QuitReason {

    /**
     * The application will be closed manually by the user, e.g. by clicking
     * the "Quit" button.
     */
    USER = "user",

    /**
     * The application will be closed because it will be reloaded (a new
     * instance is being launched).
     */
    RELOAD = "reload",

    /**
     * The application will be closed automatically, e.g. by a timer.
     */
    AUTO = "auto",

    /**
     * The application will be closed due to a critical error, e.g. during
     * early initialization.
     */
    ERROR = "error",

    /**
     * The application will be closed due to the user's logout.
     */
    LOGOUT = "logout"
}

// global events ==============================================================

declare global {
    interface DocsGlobalEventMap {

        /**
         * Will be emitted when a floating application opens or closes.
         *
         * @param visible
         *  Whether a floating application is currently hovering the main
         *  application.
         */
        "floatingapp:visible": [visible: boolean];
    }
}

// private functions ==========================================================

function createGlobalAppConfig(config: Omit<GlobalAppConfig, "portalCapabilities" | "portalAvailable" | "portalModulePath" | "editorModulePath">): GlobalAppConfig {
    const { appType } = config;
    const portalCapabilities = `${appType} infostore !${appType}portaldisabled !guest !anonymous`;
    return Object.assign(config, {
        portalCapabilities,
        portalAvailable: capabilities.has(portalCapabilities),
        portalModulePath: PORTAL_MODULE_PREFIX + appType,
        editorModulePath: DOCS_MODULE_PREFIX + appType,
    });
}

// constants ==================================================================

/**
 * Common module path prefix for all Documents applications.
 */
export const DOCS_MODULE_PREFIX = "io.ox/office/";

/**
 * Module path prefix for all Documents portal applications.
 */
export const PORTAL_MODULE_PREFIX = `${DOCS_MODULE_PREFIX}portal/`;

/**
 * Module path for the Documents Presenter application.
 */
export const PRESENTER_MODULE_PATH = DOCS_MODULE_PREFIX + "presenter";

/**
 * Global configuration of the Text application.
 */
export const TEXT_APP_CONFIG = createGlobalAppConfig({
    appType: AppType.TEXT,
    appIndex: 1,
    appIcon: "bi:file-earmark-text",
    appColor: "--app-color-text",
    appTitle: gt.pgettext("app", "Text"),
    appExtRE: FilesAPI.Model.prototype.types.doc,
    docName: gt.pgettext("document-name", "Text document"),
    docNameEncrypted: gt.pgettext("document-name", "Text document (encrypted)"),
    docNewLabel: gt.pgettext("document-name", "New text document"),
    docNewEncryptedLabel: gt.pgettext("document-name", "New text document (encrypted)"),
    docBlankLabel: gt("Blank text document"),
});

/**
 * Global configuration of the Spreadsheet application.
 */
export const SPREADSHEET_APP_CONFIG = createGlobalAppConfig({
    appType: AppType.SPREADSHEET,
    appIndex: 2,
    appIcon: "svg:spreadsheet",
    appColor: "--app-color-spreadsheet",
    appTitle: gt.pgettext("app", "Spreadsheet"),
    appExtRE: FilesAPI.Model.prototype.types.xls,
    docName: gt.pgettext("document-name", "Spreadsheet"),
    docNameEncrypted: gt.pgettext("document-name", "Spreadsheet (encrypted)"),
    docNewLabel: gt.pgettext("document-name", "New spreadsheet"),
    docNewEncryptedLabel: gt.pgettext("document-name", "New spreadsheet (encrypted)"),
    docBlankLabel: gt("Blank spreadsheet"),
});

/**
 * Global configuration of the Presentation application.
 */
export const PRESENTATION_APP_CONFIG = createGlobalAppConfig({
    appType: AppType.PRESENTATION,
    appIndex: 3,
    appIcon: "svg:presentation",
    appColor: "--app-color-presentation",
    appTitle: gt.pgettext("app", "Presentation"),
    appExtRE: FilesAPI.Model.prototype.types.ppt,
    docName: gt.pgettext("document-name", "Presentation"),
    docNameEncrypted: gt.pgettext("document-name", "Presentation (encrypted)"),
    docNewLabel: gt.pgettext("document-name", "New presentation"),
    docNewEncryptedLabel: gt.pgettext("document-name", "New presentation (encrypted)"),
    docBlankLabel: gt("Blank presentation"),
});

/**
 * Array with global configuration of all Documents portal/editor applications.
 */
export const GLOBAL_APP_CONFIGS: readonly GlobalAppConfig[] = [
    TEXT_APP_CONFIG,
    SPREADSHEET_APP_CONFIG,
    PRESENTATION_APP_CONFIG,
];

// singletons =================================================================

export const perfLogger = new Logger("office:log-perf", { defLogLevel: LogLevel.TRACE, tag: "PERF", tagColor: 0xFF8000 });

const metricsLogger = new Logger("office:log-metrics", { tag: "METRICS", tagColor: 0xFF8000 });

// all floating applications that are currently visible
const floatingApps = new Set<App>();

// private functions ==========================================================

function handleFloatingApp(app: App, insert: boolean): void {
    const wasEmpty = !floatingApps.size;
    set.toggle(floatingApps, app, insert);
    const isEmpty = !floatingApps.size;
    if (wasEmpty !== isEmpty) {
        globalEvents.emit("floatingapp:visible", !isEmpty);
    }
}

// listen to all starting applications
ox.on("app:start", app => {

    // handle applications with a floating window only
    const floatModel = app.getWindow()?.floating?.model;
    if (!floatModel) { return; }

    // register the application, and unregister it when it closes
    handleFloatingApp(app, true);
    app.on("quit", () => handleFloatingApp(app, false));

    // handle minimizing floating window
    floatModel.on("change:minimized", () => {
        handleFloatingApp(app, !floatModel.get("minimized"));
    });
});

// public functions ===========================================================

/**
 * Returns the global configuration of the specified Documents portal/editor
 * applications, if available.
 *
 * @param appType
 *  The type identifier of the Documents application.
 *
 * @returns
 *  The configuration settings of the application, if available.
 */
export function getGlobalAppConfig(appType: AppType): Readonly<GlobalAppConfig> {
    return GLOBAL_APP_CONFIGS.find(entry => entry.appType === appType)!;
}

/**
 * Parses the passed application module path, and returns data for Documents
 * applications.
 *
 * @param modulePath
 *  The module path to be parsed.
 *
 * @returns
 *  The data for a Documents application; or `undefined` for any other module
 *  path.
 */
export function parseModulePath(modulePath: string): Opt<{ category: "portal" | "editor"; type: AppType } | { category: "other"; type: string }> {

    // detect portal applications
    if (modulePath.startsWith(PORTAL_MODULE_PREFIX)) {
        const type = to.enum(AppType, modulePath.slice(PORTAL_MODULE_PREFIX.length));
        return type && { category: "portal", type };
    }

    // detect editor applications
    const rest = modulePath.startsWith(DOCS_MODULE_PREFIX) ? modulePath.slice(DOCS_MODULE_PREFIX.length) : undefined;
    const type = rest && to.enum(AppType, rest);
    if (type) { return { category: "editor", type }; }

    // other applications
    return (rest && !rest.includes("/")) ? { category: "other", type: rest } : undefined;
}

/**
 * Changes the favicon to the specified application.
 *
 * @param appType
 *  The application type identifier.
 */
export function setAppFavIcon(appType: AppType): void {

    // configuration of a Documents application favicon
    interface FavIconSpec {
        id: string;     // DOM element identifier of the `<link>` element
        rel: string;    // relation identifier for the `<link>` element
        file: string;   // filename to be used for the "href" attribute
    }

    // specifications of all application favicons to be updated
    const FAVICON_SPECS: FavIconSpec[] = [
        { id: "favicon",         rel: "icon",             file: "favicon.ico" },
        { id: "homescreen-icon", rel: "apple-touch-icon", file: "icon180.png" }
    ];

    // full path to the icon images of the specified application
    const path = `${ox.root}/io.ox/office/${appType}/resource/icons`;

    // create or update all known favicons
    for (const { id, rel, file } of FAVICON_SPECS) {
        const link = document.querySelector(`head>#${id}`) ?? document.head.appendChild(createElement("link", { id }));
        link.setAttribute("rel", rel);
        link.setAttribute("href", `${path}/${file}`);
        if (file.endsWith(".ico")) {
            link.setAttribute("type", "image/x-icon");
        }
    }
}

/**
 * Returns whether a floating application window is currently hovering the main
 * application.
 *
 * @returns
 *  Whether a floating application window is currently hovering the main
 *  application.
 */
export function hasFloatingApp(): boolean {
    return floatingApps.size > 0;
}

/**
 * Tracks a file action event for the specified application.
 *
 * @param appType
 *  The type of the application to track the event for.
 *
 * @param target
 *  The target specifier.
 *
 * @param action
 *  The action specifier.
 */
export function trackActionEvent(appType: AppType, target: string, action: string): void {
    const data = { app: `${appType}-portal`, target, action: `${appType}-${action}` };
    metricsLogger.log(`app="${data.app}" target="${data.target}" action="${data.action}"`);
    // DOCS-4110: TODO: replace old metrics with "OX Count"
    // Metrics.trackEvent({ app: `${appType}-portal`, target, action: `${appType}-${action}` });
}

/**
 * Creates a generic text file download in the browser.
 *
 * @param fileName
 *  The name of the file to be offered by the browser.
 *
 * @param contents
 *  The text contents of the file to be downloaded, as string blob, or as array
 *  of text lines which will be joined with newline characters.
 */
export function downloadTextFile(fileName: string, contents: string | string[]): void {

    // create a `Blob` object from the text contents to be downloaded
    const blob = new Blob([is.array(contents) ? str.joinTail(contents, "\n") : contents], { type: "text/plain" });
    const href = URL.createObjectURL(blob);

    // normal download code
    const downloadLink = createAnchor(href, { label: str.noI18n("Download") });
    downloadLink.download = fileName;
    insertHiddenNodes(downloadLink);

    // must be a native click on a DOM node to trigger the link
    downloadLink.click();
    downloadLink.remove();
}

/**
 * Returns the localized name for a sheet in a Spreadsheet document with the
 * specified index, ready to be used in the document model or API requests
 * (i.e., without any I18N marker characters used for UI debugging).
 *
 * @param index
 *  The one-based (!) sheet index to be inserted into the sheet name.
 *
 * @returns
 *  The generated sheet name (e.g. `Sheet1`, `Sheet2`, etc.).
 */
export function generateSheetName(index: number): string {
    //#. Default sheet names in spreadsheet documents. Should be equal to the sheet
    //#. names used in existing spreadsheet applications (Excel, OpenOffice Calc, ...).
    //#. %1$d is the numeric index inside the sheet name
    //#. Resulting names e.g. "Sheet1", "Sheet2", etc.
    //#. No white-space between the "Sheet" label and sheet index!
    return str.cleanI18n(gt("Sheet%1$d", index));
}

/**
 * In debug mode, loads the main debug module with registration entry points
 * for the application. Does not fail if the debug module does not exist. In
 * production mode, this function does nothing, regardless whether the debug
 * main module exists.
 *
 * @returns
 *  A promise that will *always* be fulfilled regardless of the result.
 */
export const importDebugMainModule = fun.once(async (): Promise<Opt<DebugCommonMainModule>> => {
    return DEBUG ? await perfLogger.takeTime("$badge{BaseApp} importDebugMainModule", async () => {
        try {
            return await import("@/io.ox/office/debug/common/main");
        } catch (err) {
            debug.logScriptError(err, "importDebugMainModule: importing debug main module failed");
            return undefined;
        }
    }) : undefined;
});

/**
 * In debug mode, invokes a callback function and passes the registration entry
 * points of the debug main module for the application. Does not fail if the
 * debug module do not exist. In production mode, this function does nothing,
 * regardless whether the debug main module exists.
 *
 * @param docApp
 *  The application instance that wants to load a debug module.
 *
 * @param fn
 *  A callback function what will be invoked with the debug module contents.
 *
 * @returns
 *  A promise that will *always* be fulfilled regardless of the result.
 */
export async function invokeDebugMainModule(docApp: BaseApplication, fn: (module: DebugCommonMainModule) => MaybeAsync): Promise<void> {
    try {
        const module = await importDebugMainModule();
        if (!module) { return; }
        // DOCS-4813: application may have been closed while the module is loaded
        if (docApp.docModel && !docApp.isInQuit()) {
            await fn(module);
        } else {
            throw new AbortError(true);
        }
    } catch (err) {
        debug.logScriptError(err, "invokeDebugMainModule: debug initialization callback failed");
    }
}
